import {
  Variant,
  DealStatus,
  DealOverview,
  AgentOverview,
  AgentOfferDetails,
  DealStage,
  OfferStatus,
  ValuationSummary,
  dealStage,
  agentsByDealStatus,
  sortByOfferDate,
} from "@flypengine/myFlyp-lib"
import * as O from "fp-ts/lib/Option"
import * as R from "fp-ts/lib/Record"
import * as A from "fp-ts/lib/Array"
import * as E from "fp-ts/lib/Either"
import { pipe } from "fp-ts/function"
import * as Router from "../../routes"
import * as N from "fp-ts/lib/number"
import { contramap, reverse } from "fp-ts/lib/Ord"
import { PageStatus } from "../../lib/types"
import { upcommingDates } from "../../lib/calendar"

export type Config<msg> = {
  propcode: string
  wrapperMsg: (m: Msg) => msg
  tabChangedMsg: (tab: Router.Tab) => msg
  dashboardChanged: (propcode: string) => msg
  dealOverviewMsg: (propcode: string) => msg
}

export type Model = E.Either<
  PageStatus,
  {
    modal: O.Option<Modal>
    propertyInfo: PropertyDetails
    statusModel: StatusModel
    propertyWorth: PropertyWorthModel
    agentsModel: O.Option<AgentsModel>
    agentViewingsModel: O.Option<AgentViewingsModel>
    upcommingViewingsModel: O.Option<UpcommingViewingsModel>
    lastFocused: O.Option<string>
  }
>

export type StatusModel = {
  statuses: Status[]
  current: DealStatus
}
export type Status = {
  id: DealStatus
  index: number
  title: string
  checklist: { item: string; complete: boolean }[]
  active: boolean
  complete: boolean
}

export type AgentsModel = {
  agents: AgentOverview[]
  stage: DealStage
  showAgents: boolean
}

export type PropertyWorthPagination = "valuation" | "offer"

export type PropertyWorthModel = {
  current: PropertyWorthPagination
  agents: AgentOverview[]
  valuations: AgentValuation[]
  offers: Record<string, AgentOfferDetails[]>
}

export type AgentOffer = {
  agent: AgentOverview
  offer: Omit<AgentOfferDetails, "agentId">
}

export type AgentValuation = {
  agent: AgentOverview
  valuation: ValuationSummary
}

export type ValuationDetail = {
  valuations: AgentValuation[]
  index: number
}

export type OfferDetail = {
  agent: AgentOverview
  offers: AgentOfferDetails[]
}

export type PropertyDetails = {
  address: string
  images: string[]
  slide: number
}

export type AgentViewingsModel = { agents: number; viewings: number }
export type UpcommingViewingsModel = {
  status: DealStatus
  week: number
  month: number
}

export const _PropertyDetails = "PropertyDetails"
export const _ValuationDetail = "ValuationDetail"
export const _OfferDetail = "OfferDetail"

export type Modal =
  | Variant.Tagged<typeof _PropertyDetails, PropertyDetails>
  | Variant.Tagged<typeof _ValuationDetail, ValuationDetail>
  | Variant.Tagged<typeof _OfferDetail, OfferDetail>

export const Modal = Variant.module<Modal>({
  PropertyDetails: value => Variant.tagged(_PropertyDetails, value),
  ValuationDetail: value => Variant.tagged(_ValuationDetail, value),
  OfferDetail: value => Variant.tagged(_OfferDetail, value),
})

export const _init = "init"
export const _lastFocused = "lastFocused"
export const _modalContents = "modalContents"
export const _agentsModel = "agentsModel"
export const _statusModel = "statusModel"
export const _propertyWorthModel = "propertyWorthModel"
export const _offersReq = "offersReq"
export const _offersRes = "offersRes"

export type Msg =
  | Variant.Tagged<typeof _init, void>
  | Variant.Tagged<typeof _modalContents, O.Option<Modal>>
  | Variant.Tagged<typeof _agentsModel, AgentsModel>
  | Variant.Tagged<typeof _statusModel, StatusModel>
  | Variant.Tagged<typeof _propertyWorthModel, PropertyWorthModel>
  | Variant.Tagged<typeof _lastFocused, O.Option<string>>

export const Msg = Variant.module<Msg>({
  init: value => Variant.tagged(_init, value),
  modalContents: value => Variant.tagged(_modalContents, value),
  agentsModel: value => Variant.tagged(_agentsModel, value),
  statusModel: value => Variant.tagged(_statusModel, value),
  propertyWorthModel: value => Variant.tagged(_propertyWorthModel, value),
  lastFocused: value => Variant.tagged(_lastFocused, value),
})

export const overviewInitModel = (
  deal: E.Either<PageStatus, DealOverview>
): Model => {
  return pipe(
    deal,
    E.map(deal => {
      const agents = agentsByDealStatus(deal)
      return {
        modal: O.none,
        propertyInfo: initPropertyModel(deal),
        statusModel: initStatus(deal, agents),
        propertyWorth: initPropertyWorthModel(deal),
        agentsModel: initAgentsModel(deal.status, agents),
        agentViewingsModel: initAgentViewingsModel(deal),
        upcommingViewingsModel: initUpcommingViewingsModel(deal),
        lastFocused: O.none,
      }
    })
  )
}

export const initPropertyModel = (deal: DealOverview): PropertyDetails => ({
  address: deal.propertyInfo.address,
  images: deal.propertyInfo.images,
  slide: 0,
})

export const initPropertyWorthModel = (
  deal: DealOverview
): PropertyWorthModel => ({
  valuations: pipe(
    deal.valuations.responses,
    A.filterMap(({ agentId, ...valuation }) =>
      pipe(
        deal.agents,
        A.findFirst(agent => agent.id === agentId),
        O.map(agent => ({ agent, valuation }))
      )
    ),
    A.sortBy([
      pipe(
        N.Ord,
        contramap(({ valuation }: AgentValuation) => valuation.propertyValue),
        reverse
      ),
    ])
  ),
  offers: deal.offers,
  agents: deal.agents,
  current: R.isEmpty(deal.offers) ? "valuation" : "offer",
})

export const initAgentsModel = (
  dealStatus: DealStatus,
  agents: AgentOverview[]
): O.Option<AgentsModel> =>
  A.isEmpty(agents)
    ? O.none
    : O.some({
        stage: dealStage(dealStatus),
        agents,
        showAgents: false,
      })

export const initAgentViewingsModel = (
  deal: DealOverview
): O.Option<AgentViewingsModel> =>
  ["agentViewings", "offers"].includes(deal.status)
    ? pipe(
        deal.viewings,
        A.reduce([], (acc: number[], vieiwng) =>
          acc.includes(vieiwng.agentId) ? acc : [...acc, vieiwng.agentId]
        ),
        agentIdList =>
          O.some({ agents: agentIdList.length, viewings: deal.viewings.length })
      )
    : O.none

export const initUpcommingViewingsModel = (
  deal: DealOverview
): O.Option<UpcommingViewingsModel> => {
  const stage = dealStage(deal.status)

  switch (stage) {
    case "prelaunch":
    case "valuation": {
      return O.none
    }

    case "instruction": {
      return O.some({
        week: upcommingDates(
          deal.viewings.map(viewing => viewing.viewingDate),
          "week"
        ).length,
        month: upcommingDates(
          deal.viewings.map(viewing => viewing.viewingDate),
          "month"
        ).length,
        status: deal.status,
      })
    }
  }
}

export const initStatus = (
  deal: DealOverview,
  agents: AgentOverview[]
): StatusModel => {
  const { instructions, valuations, valuationsRequests, viewings, offers } = {
    instructions: agents.filter(
      agent =>
        agent.instructed &&
        pipe(
          agent.agentDealStatus,
          O.fold(
            () => true,
            status => status !== "REJECTED"
          )
        )
    ).length,
    valuations: deal.valuations.responses.length,
    valuationsRequests: deal.valuations.totalRequests,
    viewings: deal.viewings.length,
    offers: pipe(
      deal.offers,
      R.filterMapWithIndex((_, offers) => A.head(offers)),
      Object.values
    ),
  }

  const contents: {
    id: DealStatus
    title: string
    checklist: { item: string; complete: boolean }[]
  }[] = [
    {
      id: "preparation",
      title: "Preparing home",
      checklist: [
        {
          item: `Assembling marketing material`,
          complete: A.isNonEmpty(agents),
        },
      ],
    },
    {
      id: "valuations",
      title: "Out for valuations",
      checklist:
        valuationsRequests === 0
          ? [
              {
                item: `${valuationsRequests} valuation requests sent`,
                complete: valuationsRequests > 0,
              },
            ]
          : [
              {
                item:
                  valuationsRequests === 1
                    ? `${valuationsRequests} valuation request sent`
                    : `${valuationsRequests} valuation requests sent`,
                complete: valuationsRequests > 0,
              },
              {
                item:
                  valuations === 1
                    ? `${valuations} valuation back`
                    : `${valuations} valuations back`,
                complete: valuations > 0,
              },
            ],
    },
    {
      id: "priceAgreement",
      title: "Agree sale strategy",
      checklist: [
        {
          item:
            valuations === 1
              ? `${valuations} valuation back`
              : `${valuations} valuations back`,
          complete: valuations > 0,
        },
      ],
    },
    {
      id: "marketplaceLaunched",
      title: "Launched!",
      checklist: [
        {
          item:
            instructions === 1
              ? `${instructions} agent instructed`
              : `${instructions} agents instructed`,
          complete: instructions > 0,
        },
      ],
    },
    {
      id: "agentViewings",
      title: "Viewings underway",
      checklist: [
        {
          item:
            viewings === 1
              ? `${viewings} viewing booked`
              : `${viewings} viewings booked`,
          complete: viewings > 0,
        },
      ],
    },
    {
      id: "offers",
      title: "Offers",
      checklist: pipe(
        offers,
        A.reduce({ accepted: 0, rejected: 0 }, (acc, offer) => {
          if (
            ["Accepted", "Under Offer", "Offer Exchanged"].includes(
              offer.status
            )
          ) {
            return { ...acc, accepted: acc.accepted + 1 }
          }

          if (offer.status === "Rejected") {
            return { ...acc, rejected: acc.rejected + 1 }
          }

          return acc
        }),
        ({ accepted, rejected }) => [
          {
            item:
              accepted === 1
                ? `${accepted} offer accepted`
                : `${accepted} offers accepted`,
            complete: accepted > 0,
          },
          {
            item:
              rejected === 1
                ? `${rejected} offer rejected`
                : `${rejected} offers rejected`,
            complete: rejected > 0,
          },
        ]
      ),
    },
  ]

  return pipe(
    contents,
    A.mapWithIndex((idx, status) => ({
      id: status.id,
      index: idx,
      title: status.title,
      checklist: status.checklist,
      active: status.id === deal.status,
      complete: pipe(
        contents,
        A.findIndex(status => deal.status === status.id),
        O.match(
          () => false,
          activeIdx => idx < activeIdx
        )
      ),
    })),
    statuses => ({ statuses, current: deal.status })
  )
}

export const filterRecentOffers = (
  offers: Record<string, AgentOfferDetails[]>,
  agents: AgentOverview[]
): AgentOffer[] =>
  pipe(
    sortByOfferDate(
      pipe(
        offers,
        R.filterMapWithIndex((_, offers) => A.head(offers)),
        Object.values
      ),
      "DESC"
    ),
    A.filterMap(({ agentId, ...offer }) =>
      pipe(
        agents,
        A.findFirst(agent => agent.id === agentId),
        O.map(agent => ({ agent, offer }))
      )
    )
  )

export const offerStatusLabel = (status: OfferStatus): string => {
  switch (status) {
    case "Accepted":
    case "Under Offer": {
      return "Accepted"
    }
    case "Offer Exchanged": {
      return "Accepted"
    }

    case "Completed":
    case "Rejected":
    case "Fell Through": {
      return status
    }

    default: {
      return "Reviewing"
    }
  }
}
